import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Common } from '@thecvlb/design-system/lib/src';

import { selectOrchestrate } from 'store';

import Heading from 'pages/SignUp/components/Heading';

import FadeWrapper from 'shared/animationWrappers/FadeWrapper';
import CheckboxGroup from 'shared/CheckboxGroup';

import { useAppSelector, useQuery } from 'hooks';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import { FlowTypes } from 'utils/enums';
import { buildSearch } from 'utils/helpers';

interface InsuranceTypesProps {
  flow?: FlowTypes | 'authorized';
  onContinue: (withInsurance: boolean) => void;
}

const ITEMS = [
  {
    label: (
      <>
        <p className="font-bold">Employer / Commercial or Exchange (ACA) Plan</p>
        <p className="text-mSm text-gray md:text-sm">IE: Aetna, Blue Cross, etc.</p>
      </>
    ),
    value: 'employer_commercial'
  },
  {
    label: (
      <>
        <p className="font-bold">Medicare or Medicaid</p>
        <p className="text-mSm text-gray md:text-sm">
          Government plans not including VA or TRICARE
        </p>
      </>
    ),
    value: 'government'
  },
  {
    label: (
      <>
        <p className="font-bold">Veterans Affairs (VA) / TRICARE</p>
        <p className="text-mSm text-gray md:text-sm">
          ie: Humana Military, Health Net Federal Services, etc
        </p>
      </>
    ),
    value: 'veterans_tricare'
  },
  {
    label: (
      <>
        <p className="font-bold">Uninsured</p>
        <p className="text-mSm text-gray md:text-sm">I do not have health insurance</p>
      </>
    ),
    value: 'uninsured'
  }
];

const InsuranceTypes: React.FC<InsuranceTypesProps> = ({ onContinue, flow }) => {
  const query = useQuery();
  const { mif_qa: mifQA } = useAppSelector(selectOrchestrate);
  const { send, isLoading, handleProspect } = useSubmitOrchestrateForm();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const handleSubmit = () => {
    if (!selectedItems.includes('government')) {
      const body: {
        answer: string | string[];
        question: string;
      }[] = [{ answer: selectedItems, question: 'insurance_types' }];
      if (selectedItems.includes('uninsured')) {
        body.push({ answer: 'no', question: 'use_insurance_for_appointment' });
      }
      send('mif_qa', body, () => {
        onContinue(!selectedItems.includes('uninsured'));
      });
    } else {
      return navigate({
        search: buildSearch(
          flow === 'authorized' ? 's=wm-insurance-types&' : '',
          `hideProgress=true&subStep=insurance-types`
        )
      });
    }
  };

  const isErrorState = query.get('subStep') === 'insurance-types';

  const handleSelect = (v: number | string) => {
    let newArray: string[] = [];
    const stringValue = String(v);
    if (v === 'uninsured') {
      if (!selectedItems.includes('uninsured')) {
        newArray = [v];
      }
    } else {
      newArray = selectedItems.includes(stringValue)
        ? selectedItems.filter((i) => i !== stringValue && i !== 'uninsured')
        : [...selectedItems, stringValue].filter((i) => i !== 'uninsured');
    }

    setSelectedItems(newArray);
  };

  useEffect(() => {
    const insurancesAnswer = mifQA.find((v) => v.question === 'insurance_types');
    if (insurancesAnswer && Array.isArray(insurancesAnswer.answer)) {
      setSelectedItems(insurancesAnswer.answer);
    }
  }, []);

  return (
    <div className="onboarding-page-wrapper">
      {isErrorState && (
        <FadeWrapper className="onboarding-page-wrapper text-primary-700" key="forbidden">
          <div className="flex flex-col gap-1 text-primary-700">
            <h4 className="wm-signup-title">
              Sorry, we do not accept Medicare or Medicaid for GLP-1 treatment at this time.
            </h4>
            <span>
              We are currently unable to accept patients covered by Medicare or Medicaid insurance
              (including primary, secondary, or Medicare Supplement Plans) in the LifeMD GLP-1
              weight loss program.
            </span>
          </div>
          <Common.Button
            className="self-center !text-gray-700"
            isLoading={isLoading}
            style="text-only"
            onClick={() =>
              handleProspect({
                answer: false,
                question: 'insurance-types'
              })
            }
          >
            Back to LifeMD website
          </Common.Button>
        </FadeWrapper>
      )}
      {!isErrorState && (
        <>
          <Heading category="Plan" title="Which type of health insurance do you have?" />
          <div>
            <CheckboxGroup items={ITEMS} selectedItems={selectedItems} onSelect={handleSelect} />
          </div>
          <Common.Button
            className="max-md:mt-auto md:mx-auto"
            color="blue"
            disabled={selectedItems.length === 0}
            isLoading={isLoading}
            fullWidthOnMobile
            onClick={handleSubmit}
          >
            Continue
          </Common.Button>
        </>
      )}
    </div>
  );
};

export default InsuranceTypes;
